import { CUSTOM_ELEMENTS_SCHEMA, Component, Inject, OnInit, signal } from '@angular/core';
import { BasePageComponent } from '../../../../../../shared/base/base-page.component';
import { AreYouSureFormGroup } from '../../../../../../shared/payments/are-you-sure-radio-buttons/are-you-sure-form-group';
import { SessionService } from '../../../../../../shared/session/session.service';
import { Router } from '@angular/router';
import { ManagePaymentPrefsFormBuilder } from '../../../../manage/base/manage-payment-prefs-form-builder';
import { BillingCommonNavigationsService } from '../../../../../shared/billing-common-navigations.service';
import { environment } from '../../../../../../../environments/environment';
import { UrlUtil } from '../../../../../../shared/url-util/url-util.service';
import { ManagePaymentPreferencesPageTitle } from '../../../../manage/shared/models/manage-payment-preferences-page-title.constant';
import { FormUtil } from '../../../../../../shared/payments/form-util';
import { CancelScheduleFuturePaymentService } from '../services/cancel-schedule-future-payment.service';
import { CancelScheduleFuturePaymentFlow } from '../services/cancel-schedule-future-payment-flow.service';
import { PaymentPreferencesStateService } from '../../../../../../billing/payment-preferences/shared/services/payment-preferences-state/payment-preferences-state.service';
import { LoggerService } from '../../../../../../shared/logger/logger.service';
import { CurrencyPipe } from '@angular/common';
import { CelebrusTrackingService } from '../../../../../../shared/service/celebrus-tracking/celebrus-tracking.service';
import { WaitMessageComponent } from '@nationwide/internet-servicing-angular-components';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FocusOnInitDirective } from '../../../../../../shared/attr-directives/focus-on-init.directive';
import { PaymentSubHeaderComponent } from '../../../../../../shared/payments/payment-sub-header/payment-sub-header.component';
import { AreYouSureRadioButtonsComponent } from '../../../../../../shared/payments/are-you-sure-radio-buttons/are-you-sure-radio-buttons.component';
import { isbExperienceServiceToken, IsbExperienceService } from '@nationwide/dgs-angular-billing-common';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
    selector: 'app-scheduled-future-payments-landing',
    templateUrl: './cancel-scheduled-future-payment-landing.component.html',
    styleUrls: [
        './cancel-scheduled-future-payment-landing.component.scss'
    ],
    imports: [WaitMessageComponent, FormsModule, ReactiveFormsModule, FocusOnInitDirective, PaymentSubHeaderComponent, AreYouSureRadioButtonsComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CancelScheduledFuturePaymentLandingComponent extends BasePageComponent implements OnInit {
    pageLoadComplete = signal(false);
    submitted = signal(false);
    formGroup: AreYouSureFormGroup;
    pageName = ManagePaymentPreferencesPageTitle.scheduledFuturePayments.cancel.landing;

    constructor(
        router: Router,
        session: SessionService,
        private formBuilder: ManagePaymentPrefsFormBuilder,
        private navigation: BillingCommonNavigationsService,
        private urlUtil: UrlUtil,
        private formUtils: FormUtil,
        private cancelScheduleFuturePaymentService: CancelScheduleFuturePaymentService,
        private flow: CancelScheduleFuturePaymentFlow,
        private state: PaymentPreferencesStateService,
        @Inject('logger') private logger: LoggerService,
        private currencyPipe: CurrencyPipe,
        private celebrusTrackingService: CelebrusTrackingService,
        @Inject(isbExperienceServiceToken) private isbExperience: IsbExperienceService
    ) {
        super(session, router);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.formGroup = this.formBuilder.newAreYouSureFormGroup();
        this.cancelScheduleFuturePaymentService.retrieveScheduledPayment().subscribe({
            next: () => this.pageLoadComplete.set(true),
            error: (error) => {
                this.pageLoadComplete.set(true);
                this.logger.error('HARDFALL: CANCEL SCHEDULED FUTURE PAYMENT LANDING API ERROR', { error });
                this.navigation.navigateToHardfall();
            }
        });
        try {
            this.celebrusTrackingService.storeBillingAccountRole();
        } catch (error) {
            this.logger.error('Failed to store Celebrus tracking data on cancel scheduled payment confirmation', error);
        }
    }

    onCancelButton(): void {
        this.navigation.navigateToPaymentPreferences();
    }

    onSubmit(): void {
        this.submitted.set(true);
        this.formUtils.touchAllFormControls(this.formGroup.group);
        if (this.formGroup.group.valid) {
            if (this.formGroup.selection === 'yes') {
                this.cancelPayment();
            } else if (this.formGroup.selection === 'no') {
                this.cancelScheduleFuturePayment();
            }
        }
    }

    cancelScheduleFuturePayment(): void {
        this.state.resetState();
        this.flow.reset();
        this.navigation.navigateToPaymentPreferences();
    }

    cancelPayment(): void {
        this.pageLoadComplete.set(false);
        this.cancelScheduleFuturePaymentService.submit().subscribe({
            complete: () => {
                this.deleteCamCache().subscribe({
                    next: () => this.navigateToConfirmation(),
                    error: () => this.navigateToConfirmation()
                });
            }
        });
    }

    navigateToConfirmation(): void {
        this.router.navigateByUrl(
            environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.SCHEDULED_FUTURE_PAYMENTS.CANCEL.CONFIRMATION(this.urlUtil.hashParamsString)
        );
    }

    private deleteCamCache(): Observable<any> {
        return this.isbExperience.deleteCamCache({
            ecn: this.session.ecn,
            accessToken: this.session.accessToken
        }).pipe(
            tap((response) => {
                this.logger.info('CAM Cache deleted successfully in Cancel Scheduled Future Payment flow', response);
            }),
            catchError((error) => {
                this.logger.error('CAM Cache deletion failed in Cancel Scheduled Future Payment flow', {
                    error: error.error || error
                });
                return of(null);
            })
        );
    }

    get paymentAmount(): string {
        return this.flow && this.flow.paymentAmount;
    }

    get paymentDate(): string {
        return this.flow && this.flow.paymentDate;
    }

    get displayMessageForForm(): string {
        let formattedAmount = '$0.00';

        if (this.paymentAmount) {
            formattedAmount = this.paymentAmount.includes('$') ? this.paymentAmount : this.currencyPipe.transform(this.paymentAmount, 'USD', 'symbol');
        }
        return `Are you sure you want to cancel this payment of ${formattedAmount}, scheduled for ${this.paymentDate}?`;
    }

    get globalErrorDisplayable(): boolean {
        return !this.formGroup.group.valid && this.submitted();
    }
}
